import React from 'react';
import { AlertCircle, Inbox, Loader2, X } from 'lucide-react';
import SkeletonBlock from '../../components/common/SkeletonBlock';

export const inputClass = 'h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none transition focus:border-sky-400 focus:ring-2 focus:ring-sky-100';

export const labelize = (value) => String(value || 'Not set')
  .replace(/_/g, ' ')
  .replace(/\b\w/g, (char) => char.toUpperCase());

export const formatNumber = (value) => new Intl.NumberFormat('en-US').format(Number(value) || 0);

export const formatDate = (value, fallback = 'Not set') => {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
};

export const formatDateTime = (value, fallback = 'Not set') => {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  }).format(date);
};

export const formatCurrency = (amount, currency = 'USD') => {
  const numeric = Number(amount);
  if (!Number.isFinite(numeric) || numeric <= 0) return 'Not set';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: String(currency || 'USD').toUpperCase(),
    maximumFractionDigits: 0
  }).format(numeric);
};

export const extractError = (error, fallback) => (
  error?.response?.data?.error ||
  error?.response?.error ||
  error?.response?.message ||
  error?.message ||
  error?.error ||
  fallback
);

export const statusTone = (status) => {
  const normalized = String(status || '').toLowerCase();
  if (['active', 'accepted', 'completed', 'confirmed', 'reviewed', 'passed', 'signed'].includes(normalized)) {
    return 'border-emerald-100 bg-emerald-50 text-emerald-700';
  }
  if (['scheduled', 'sent', 'viewed', 'assigned', 'in_progress', 'candidate_confirmed', 'employer_confirmed'].includes(normalized)) {
    return 'border-sky-100 bg-sky-50 text-sky-700';
  }
  if (['draft', 'reschedule_requested', 'negotiation_pending', 'negotiation_countered', 'timed_out', 'expired'].includes(normalized)) {
    return 'border-amber-100 bg-amber-50 text-amber-700';
  }
  if (['cancelled', 'candidate_declined', 'employer_cancelled', 'no_show', 'rejected', 'declined', 'withdrawn', 'terminated', 'failed'].includes(normalized)) {
    return 'border-rose-100 bg-rose-50 text-rose-700';
  }
  return 'border-slate-200 bg-slate-50 text-slate-600';
};

export const StatusBadge = ({ status }) => (
  <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${statusTone(status)}`}>
    {labelize(status)}
  </span>
);

export const MetricCard = ({ icon: Icon, label, value, detail }) => (
  <div className="bg-white rounded-[24px] p-6 shadow-sm border border-slate-100 flex flex-col justify-between hover:shadow-md transition-shadow">
    <div className="flex justify-between items-start mb-6">
      <h3 className="text-sm font-semibold text-slate-600">{label}</h3>
      <div className="w-10 h-10 rounded-2xl bg-blue-50 flex items-center justify-center text-blue-600 border border-blue-100/50">
        <Icon className="w-5 h-5" />
      </div>
    </div>
    <div>
      <p className="text-3xl font-bold text-slate-900 tracking-tight">{value}</p>
      {detail && <p className="text-xs font-medium text-slate-400 mt-2">{detail}</p>}
    </div>
  </div>
);

export const DetailItem = ({ label, value }) => (
  <div className="rounded-lg border border-slate-100 bg-slate-50 p-3">
    <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-400">{label}</p>
    <p className="mt-1 whitespace-pre-wrap break-words text-sm font-medium text-slate-800">{value || 'Not set'}</p>
  </div>
);

export const EmptyState = ({ title, copy, icon: Icon = Inbox, onReset }) => (
  <div className="flex min-h-[320px] flex-col items-center justify-center rounded-[24px] border border-dashed border-slate-200 bg-white px-6 text-center">
    <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-slate-50 text-slate-500">
      <Icon className="h-6 w-6" />
    </div>
    <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
    <p className="mt-2 max-w-md text-sm leading-6 text-slate-500">{copy}</p>
    {onReset && (
      <button type="button" onClick={onReset} className="mt-5 inline-flex h-10 items-center rounded-lg bg-slate-950 px-4 text-sm font-semibold text-white transition hover:bg-slate-800">
        Reset filters
      </button>
    )}
  </div>
);

export const ErrorBanner = ({ message }) => {
  if (!message) return null;
  return (
    <div className="rounded-[24px] border border-rose-100 bg-rose-50 p-4 text-sm text-rose-700">
      <div className="flex gap-3">
        <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
        <span>{message}</span>
      </div>
    </div>
  );
};

export const PageSkeleton = () => (
  <div className="min-h-full bg-[#f8f9fa] px-4 pb-10 pt-20 sm:px-6 lg:px-8 lg:pt-8">
    <div className="mx-auto max-w-[1400px] space-y-5">
      <div className="space-y-3">
        <SkeletonBlock className="h-4 w-24 rounded-md" />
        <SkeletonBlock className="h-9 w-72 rounded-lg" />
        <SkeletonBlock className="h-4 w-full max-w-2xl rounded-md" />
      </div>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {[0, 1, 2, 3].map((item) => <SkeletonBlock key={item} className="h-28 rounded-[24px]" />)}
      </div>
      <SkeletonBlock className="h-[540px] rounded-[24px]" />
    </div>
  </div>
);

export const DetailModalShell = ({ title, subtitle, loading, onClose, children }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 p-4 backdrop-blur-sm">
    <section className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-[24px] bg-white shadow-2xl">
      <div className="flex items-start justify-between gap-4 border-b border-slate-100 p-5">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-400">Details</p>
          <h2 className="mt-2 truncate text-2xl font-semibold tracking-tight text-slate-900">{title}</h2>
          {subtitle && <p className="mt-1 text-sm text-slate-500">{subtitle}</p>}
        </div>
        <button type="button" onClick={onClose} className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-50 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900">
          <X className="h-5 w-5" />
        </button>
      </div>
      <div className="overflow-y-auto p-5">
        {loading ? (
          <div className="flex min-h-[260px] items-center justify-center text-slate-500">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            Loading details
          </div>
        ) : children}
      </div>
    </section>
  </div>
);
